import React from "react";
import { Modal, Button, Input, Tooltip, Tag, Radio, Select } from "antd";
import type { RadioChangeEvent } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
// REDUX
import { useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import socketEmitter from "../socketConnection/MonitoringWebSocketEmitter";
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { FaultContent } from "../../../flatbuffers/generated/fault/fault-content";
import { Fault } from "../../../flatbuffers/generated/fault/fault";
import { FaultVariant } from "../../../flatbuffers/generated/fault/fault-variant";
import { DeviceTrafficMessage } from "../../../flatbuffers/generated/traffic/device-traffic-message";
import { Traffic } from "../../../flatbuffers/generated/traffic/traffic";
import { TrafficVariant } from "../../../flatbuffers/generated/traffic/traffic-variant";
import { DeviceControlMessage } from "../../../flatbuffers/generated/control/device-control-message";
import { Control } from "../../../flatbuffers/generated/control/control";
import { ControlVariant } from "../../../flatbuffers/generated/control/control-variant";
import { DeviceUsageContent } from "../../../flatbuffers/generated/usage/device-usage-content";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { UsageVariant } from "../../../flatbuffers/generated/usage/usage-variant";
import { DeviceServiceContent } from "../../../flatbuffers/generated/service/device-service-content";
import { Service } from "../../../flatbuffers/generated/service/service";
import { ServiceVariant } from "../../../flatbuffers/generated/service/service-variant";
import { DeviceStatusContent } from "../../../flatbuffers/generated/status/device-status-content";
import { Status } from "../../../flatbuffers/generated/status/status";
import { StatusVariant } from "../../../flatbuffers/generated/status/status-variant";
import { DeviceGroupSupervisoryStatusContent } from "../../../flatbuffers/generated/group-supervisory-status/device-group-supervisory-status-content";
import { GroupSupervisoryStatus } from "../../../flatbuffers/generated/group-supervisory-status/group-supervisory-status";
import { GroupSupervisoryStatusVariant } from "../../../flatbuffers/generated/group-supervisory-status/group-supervisory-status-variant";
import { DeviceGroupControlContent } from "../../../flatbuffers/generated/group-control/device-group-control-content";
import { GroupControl } from "../../../flatbuffers/generated/group-control/group-control";
import { GroupControlVariant } from "../../../flatbuffers/generated/group-control/group-control-variant";
import { DeviceControllerInfoContent } from "../../../flatbuffers/generated/controller-info/device-controller-info-content";
import { ControllerInfoContent } from "../../../flatbuffers/generated/controller-info/controller-info-content";
import { ControllerInfoVariant } from "../../../flatbuffers/generated/controller-info/controller-info-variant";
import { Value } from "../../../flatbuffers/generated/value/value";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";

type DecodedFrontEndMessagesModalProps = {
  open: boolean;
  onClose: () => void;
};

type PreDispatchMessage = {
  timestamp: number;
  bankId: string;
  liftId: string;
  schemaFrontEndMessageJson: string;
  socketMessageLiftJson: string;
};

type ContentTypeFilter = "all" | "OneHotMessage" | "BitMaskMessage";

// "liftId" (default) keeps the existing single-lift debug view. "bankId" captures
// every message for every lift in the bank — needed to confirm whether a message
// family (e.g. hall calls) is actually arriving on the wire for the bank at all,
// as opposed to being dropped/filtered downstream for one particular lift.
type PreDispatchFilterMode = "liftId" | "bankId";

type DecodedMessageMetadata = {
  contentType: "OneHotMessage" | "BitMaskMessage" | null;
  messageTypeName: string | null;
  specificValueName: string | null;
};

const ONE_HOT_MESSAGE_TYPE_OPTIONS = [
  { value: "all", label: "All OneHot messages" },
  { value: "Usage_UsageMessage", label: "Usage" },
  { value: "Service_ServiceMessage", label: "Service" },
];

const BIT_MASK_MESSAGE_TYPE_OPTIONS = [
  { value: "all", label: "All BitMask messages" },
  { value: "Traffic_TrafficMessage", label: "Traffic" },
  { value: "Status_StatusMessage", label: "Status" },
  { value: "Control_ControlMessage", label: "Control" },
  { value: "Fault_FaultMessage", label: "Fault" },
  {
    value: "GroupSupervisoryStatus_GroupSupervisoryStatusMessage",
    label: "Group Supervisory Status",
  },
  { value: "GroupControl_GroupControlMessage", label: "Group Control" },
  { value: "ControllerInfo_ControllerInfoMessage", label: "Controller Info" },
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const resolveEnumValueName = (
  enumObject: Record<string, string | number>,
  rawValue: unknown,
): string | null => {
  if (typeof rawValue === "number") {
    return String(getEnumName(enumObject, rawValue));
  }
  if (typeof rawValue === "string") {
    return rawValue;
  }
  return null;
};

const resolveLeafValueName = (
  enumObject: Record<string, string | number>,
  rawValue: unknown,
): string | null => {
  const direct = resolveEnumValueName(enumObject, rawValue);
  if (direct) return direct;

  if (!isRecord(rawValue)) return null;

  const fromMessage = resolveEnumValueName(enumObject, rawValue.message);
  if (fromMessage) return fromMessage;

  if (!isRecord(rawValue.message)) return null;

  return resolveEnumValueName(enumObject, rawValue.message.message);
};

const getEnumName = (
  enumObject: Record<string, string | number>,
  value: unknown,
): string | number | unknown => {
  if (typeof value !== "number") return value;
  const enumName = (enumObject as unknown as Record<number, string>)[value];
  return enumName ?? value;
};

const getContentTypeFromSchemaJson = (
  schemaFrontEndMessageJson: string,
): "OneHotMessage" | "BitMaskMessage" | null => {
  try {
    const parsed = JSON.parse(schemaFrontEndMessageJson) as Record<string, unknown>;
    const msg = parsed.msg as Record<string, unknown> | undefined;
    const encoded = msg?.msg as Record<string, unknown> | undefined;
    const contentType = encoded?.contentType;

    if (contentType === EncodedMessageContent.OneHotMessage || contentType === "OneHotMessage") {
      return "OneHotMessage";
    }
    if (contentType === EncodedMessageContent.BitMaskMessage || contentType === "BitMaskMessage") {
      return "BitMaskMessage";
    }
    return null;
  } catch {
    return null;
  }
};

export const getDecodedMessageMetadata = (
  schemaFrontEndMessageJson: string,
): DecodedMessageMetadata => {
  try {
    const parsed = JSON.parse(schemaFrontEndMessageJson) as Record<string, unknown>;
    const msg = isRecord(parsed.msg) ? parsed.msg : null;
    const encoded = msg && isRecord(msg.msg) ? msg.msg : null;
    if (!encoded) {
      return { contentType: null, messageTypeName: null, specificValueName: null };
    }

    const contentType = getContentTypeFromSchemaJson(schemaFrontEndMessageJson);
    const content = isRecord(encoded.content) ? encoded.content : null;
    const messageTypeName = content
      ? resolveEnumValueName(MessageContent, content.messageType)
      : null;

    if (!contentType || !content || !messageTypeName) {
      return { contentType, messageTypeName, specificValueName: null };
    }

    const nestedMessage = isRecord(content.message) ? content.message : null;
    let specificValueName: string | null = null;

    // Every device message (ElevatorXMessage/EscalatorXMessage) wraps its actual
    // enum inside a NormalX/ValuedX variant table, one level deeper than the
    // device message itself. `variantFieldName` is the field that holds that
    // variant table (it is not always called "message").
    const getVariantLeaf = (
      deviceObject: Record<string, unknown> | null,
      variantFieldName: string,
    ): Record<string, unknown> | null =>
      deviceObject && isRecord(deviceObject[variantFieldName])
        ? (deviceObject[variantFieldName] as Record<string, unknown>)
        : null;

    // `messageTypeName` alone determines the nested shape below — the LN2 schema
    // wraps the exact same `MessageContent` union inside BOTH OneHotMessage and
    // BitMaskMessage (`table OneHotMessage { message: MessageContent }` /
    // `table BitMaskMessage { message: MessageContent }`), so a given message
    // family (e.g. Usage_UsageMessage) is not restricted to one wrapper. Gating
    // these branches on `contentType` as well used to silently skip messages
    // whenever a family showed up under the "other" wrapper on the wire.
    if (messageTypeName === "Usage_UsageMessage") {
      const usageDevice =
        nestedMessage && isRecord(nestedMessage.message) ? nestedMessage.message : null;
      const usageVariant = getVariantLeaf(usageDevice, "usage");
      specificValueName = resolveLeafValueName(Usage, usageVariant?.message);
    }

    if (messageTypeName === "Service_ServiceMessage") {
      const serviceDevice =
        nestedMessage && isRecord(nestedMessage.message) ? nestedMessage.message : null;
      const serviceVariant = getVariantLeaf(serviceDevice, "message");
      specificValueName = resolveLeafValueName(Service, serviceVariant?.message);
    }

    if (messageTypeName === "Traffic_TrafficMessage") {
      const trafficDevice =
        nestedMessage && isRecord(nestedMessage.message) ? nestedMessage.message : null;
      const trafficVariant = getVariantLeaf(trafficDevice, "traffic");
      specificValueName = resolveLeafValueName(Traffic, trafficVariant?.message);
    }

    if (messageTypeName === "Control_ControlMessage") {
      const controlDevice =
        nestedMessage && isRecord(nestedMessage.control) ? nestedMessage.control : null;
      const controlVariant = getVariantLeaf(controlDevice, "control");
      specificValueName = resolveLeafValueName(Control, controlVariant?.message);
    }

    if (messageTypeName === "Fault_FaultMessage") {
      const faultDevice =
        nestedMessage && isRecord(nestedMessage.fault) ? nestedMessage.fault : null;
      const faultVariant = getVariantLeaf(faultDevice, "fault");
      specificValueName = resolveLeafValueName(Fault, faultVariant?.message);
    }

    if (messageTypeName === "Status_StatusMessage") {
      const statusRoot =
        nestedMessage && isRecord(nestedMessage.status) ? nestedMessage.status : null;
      const statusLeaf = getVariantLeaf(statusRoot, "status");
      specificValueName = resolveLeafValueName(Status, statusLeaf?.message ?? statusRoot?.message);
    }

    if (messageTypeName === "GroupSupervisoryStatus_GroupSupervisoryStatusMessage") {
      const groupSupervisoryStatusDevice =
        nestedMessage && isRecord(nestedMessage.message) ? nestedMessage.message : null;
      const groupSupervisoryStatusVariant = getVariantLeaf(groupSupervisoryStatusDevice, "status");
      specificValueName = resolveLeafValueName(
        GroupSupervisoryStatus,
        groupSupervisoryStatusVariant?.message,
      );
    }

    if (messageTypeName === "GroupControl_GroupControlMessage") {
      const groupControlDevice =
        nestedMessage && isRecord(nestedMessage.message) ? nestedMessage.message : null;
      const groupControlVariant = getVariantLeaf(groupControlDevice, "control");
      specificValueName = resolveLeafValueName(GroupControl, groupControlVariant?.message);
    }

    if (messageTypeName === "ControllerInfo_ControllerInfoMessage") {
      const controllerInfoDevice =
        nestedMessage && isRecord(nestedMessage.message) ? nestedMessage.message : null;
      const controllerInfoVariant = getVariantLeaf(controllerInfoDevice, "info");
      specificValueName = resolveEnumValueName(
        ControllerInfoContent,
        controllerInfoVariant?.messageType,
      );
    }

    return { contentType, messageTypeName, specificValueName };
  } catch {
    return { contentType: null, messageTypeName: null, specificValueName: null };
  }
};

export const translateDecodedPayloadEnums = (payload: unknown): unknown => {
  if (!payload || typeof payload !== "object") return payload;

  const translated = JSON.parse(JSON.stringify(payload)) as Record<string, unknown>;
  const msg = translated.msg;
  if (!msg || typeof msg !== "object") return translated;

  const msgObject = msg as Record<string, unknown>;
  const encodedMsg = msgObject.msg;
  if (!encodedMsg || typeof encodedMsg !== "object") return translated;

  const encodedMsgObject = encodedMsg as Record<string, unknown>;
  const rawContentType = encodedMsgObject.contentType;
  encodedMsgObject.contentType = getEnumName(EncodedMessageContent, rawContentType);

  const content = encodedMsgObject.content;
  if (!content || typeof content !== "object") return translated;

  const contentObject = content as Record<string, unknown>;
  const rawMessageType = contentObject.messageType;
  contentObject.messageType = getEnumName(MessageContent, rawMessageType);

  const nestedMessage = contentObject.message;
  if (!nestedMessage || typeof nestedMessage !== "object") return translated;

  const nestedMessageObject = nestedMessage as Record<string, unknown>;
  const rawNestedMessageType = nestedMessageObject.messageType;

  // Every device message (Elevator*Message/Escalator*Message) wraps its leaf
  // enum one level deeper, inside a NormalX/ValuedX variant table (e.g.
  // ElevatorControlMessage.control -> NormalControl/ValuedControl.message).
  // ValuedX variants also carry a `valueType`/`value` payload alongside the
  // enum, which we translate too.
  const translateVariantLeaf = (
    deviceObject: unknown,
    variantTypeFieldName: string,
    variantFieldName: string,
    variantEnumObject: Record<string, string | number>,
    leafEnumObject: Record<string, string | number>,
  ): void => {
    if (!deviceObject || typeof deviceObject !== "object") return;
    const deviceRecord = deviceObject as Record<string, unknown>;

    deviceRecord[variantTypeFieldName] = getEnumName(
      variantEnumObject,
      deviceRecord[variantTypeFieldName],
    );

    const variant = deviceRecord[variantFieldName];
    if (!variant || typeof variant !== "object") return;

    const variantObject = variant as Record<string, unknown>;
    const rawLeafMessage = variantObject.message;
    if (typeof rawLeafMessage === "number") {
      variantObject.message = getEnumName(leafEnumObject, rawLeafMessage);
    }

    const rawValueType = variantObject.valueType;
    if (typeof rawValueType === "number") {
      variantObject.valueType = getEnumName(Value, rawValueType);
    }
  };

  // `rawMessageType` alone determines which nested shape follows — the LN2
  // schema wraps the exact same `MessageContent` union inside BOTH
  // OneHotMessage and BitMaskMessage, so a given message family (e.g.
  // Usage_UsageMessage) is not restricted to one wrapper on the wire. Gating
  // these branches on `rawContentType` as well used to silently skip
  // translation whenever a family showed up under the "other" wrapper.
  if (typeof rawMessageType === "number" && rawMessageType === MessageContent.Usage_UsageMessage) {
    nestedMessageObject.messageType = getEnumName(DeviceUsageContent, rawNestedMessageType);
    translateVariantLeaf(nestedMessageObject.message, "usageType", "usage", UsageVariant, Usage);
  }

  if (
    typeof rawMessageType === "number" &&
    rawMessageType === MessageContent.Control_ControlMessage
  ) {
    nestedMessageObject.controlType = getEnumName(
      DeviceControlMessage,
      nestedMessageObject.controlType,
    );
    translateVariantLeaf(
      nestedMessageObject.control,
      "controlType",
      "control",
      ControlVariant,
      Control,
    );
  }

  if (
    typeof rawMessageType === "number" &&
    rawMessageType === MessageContent.Service_ServiceMessage
  ) {
    nestedMessageObject.messageType = getEnumName(DeviceServiceContent, rawNestedMessageType);
    translateVariantLeaf(
      nestedMessageObject.message,
      "messageType",
      "message",
      ServiceVariant,
      Service,
    );
  }

  if (
    typeof rawMessageType === "number" &&
    rawMessageType === MessageContent.Traffic_TrafficMessage
  ) {
    nestedMessageObject.messageType = getEnumName(DeviceTrafficMessage, rawNestedMessageType);
    translateVariantLeaf(
      nestedMessageObject.message,
      "trafficType",
      "traffic",
      TrafficVariant,
      Traffic,
    );
  }

  if (
    typeof rawMessageType === "number" &&
    rawMessageType === MessageContent.Status_StatusMessage
  ) {
    nestedMessageObject.statusType = getEnumName(
      DeviceStatusContent,
      nestedMessageObject.statusType,
    );
    translateVariantLeaf(nestedMessageObject.status, "statusType", "status", StatusVariant, Status);
  }

  if (
    typeof rawMessageType === "number" &&
    rawMessageType === MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage
  ) {
    nestedMessageObject.messageType = getEnumName(
      DeviceGroupSupervisoryStatusContent,
      nestedMessageObject.messageType,
    );
    translateVariantLeaf(
      nestedMessageObject.message,
      "statusType",
      "status",
      GroupSupervisoryStatusVariant,
      GroupSupervisoryStatus,
    );
  }

  if (
    typeof rawMessageType === "number" &&
    rawMessageType === MessageContent.GroupControl_GroupControlMessage
  ) {
    nestedMessageObject.messageType = getEnumName(
      DeviceGroupControlContent,
      nestedMessageObject.messageType,
    );
    translateVariantLeaf(
      nestedMessageObject.message,
      "controlType",
      "control",
      GroupControlVariant,
      GroupControl,
    );
  }

  if (
    typeof rawMessageType === "number" &&
    rawMessageType === MessageContent.ControllerInfo_ControllerInfoMessage
  ) {
    nestedMessageObject.messageType = getEnumName(
      DeviceControllerInfoContent,
      nestedMessageObject.messageType,
    );

    // ControllerInfo's variant leaf holds a `messageType` (which union-typed
    // table is present) and the table itself under `message`, rather than a
    // plain numeric enum - so it needs its own handling instead of
    // translateVariantLeaf.
    const nestedControllerInfo = nestedMessageObject.message;
    if (nestedControllerInfo && typeof nestedControllerInfo === "object") {
      const nestedControllerInfoObject = nestedControllerInfo as Record<string, unknown>;
      nestedControllerInfoObject.infoType = getEnumName(
        ControllerInfoVariant,
        nestedControllerInfoObject.infoType,
      );

      const infoVariant = nestedControllerInfoObject.info;
      if (infoVariant && typeof infoVariant === "object") {
        const infoVariantObject = infoVariant as Record<string, unknown>;
        const rawControllerInfoMessageType = infoVariantObject.messageType;
        if (typeof rawControllerInfoMessageType === "number") {
          infoVariantObject.messageType = getEnumName(
            ControllerInfoContent,
            rawControllerInfoMessageType,
          );
        }

        const rawValueType = infoVariantObject.valueType;
        if (typeof rawValueType === "number") {
          infoVariantObject.valueType = getEnumName(Value, rawValueType);
        }
      }
    }
  }

  if (typeof rawMessageType === "number" && rawMessageType === MessageContent.Fault_FaultMessage) {
    nestedMessageObject.faultType = getEnumName(FaultContent, nestedMessageObject.faultType);
    translateVariantLeaf(nestedMessageObject.fault, "faultType", "fault", FaultVariant, Fault);
  }

  return translated;
};

const getServiceModeTagInfo = (
  socketMessageLiftJson: string,
): ReturnType<typeof getServiceModeDisplay> => {
  try {
    const parsed = JSON.parse(socketMessageLiftJson) as Record<string, unknown>;
    const serviceMode = parsed.serviceMode;
    if (!serviceMode || typeof serviceMode !== "object") return null;

    const serviceModeObject = serviceMode as Record<string, unknown>;
    const serviceContent = serviceModeObject.messageType;
    const serviceMessageContainer = serviceModeObject.message;
    if (
      typeof serviceContent !== "number" ||
      !serviceMessageContainer ||
      typeof serviceMessageContainer !== "object"
    ) {
      return null;
    }

    const serviceMessage = (serviceMessageContainer as Record<string, unknown>).message;
    if (typeof serviceMessage !== "number") return null;

    return getServiceModeDisplay(serviceContent as DeviceServiceContent, serviceMessage as Service);
  } catch {
    return null;
  }
};

const omitBankIdAndLiftId = (payload: unknown): unknown => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return payload;
  const { bankId: _bankId, liftId: _liftId, ...rest } = payload as Record<string, unknown>;
  return rest;
};

function DecodedFrontEndMessagesModal({
  open,
  onClose,
}: DecodedFrontEndMessagesModalProps): React.ReactElement {
  const { theme } = useSelector((state: RootState) => state.home);
  const [preDispatchMessages, setPreDispatchMessages] = React.useState<PreDispatchMessage[]>([]);
  const [preDispatchFilterMode, setPreDispatchFilterMode] =
    React.useState<PreDispatchFilterMode>("liftId");
  const [preDispatchLiftFilter, setPreDispatchLiftFilter] = React.useState("");
  const [preDispatchBankFilter, setPreDispatchBankFilter] = React.useState("");
  const [contentTypeFilter, setContentTypeFilter] = React.useState<ContentTypeFilter>("all");
  const [oneHotMessageTypeFilter, setOneHotMessageTypeFilter] = React.useState<string>("all");
  const [bitMaskMessageTypeFilter, setBitMaskMessageTypeFilter] = React.useState<string>("all");
  const [specificValueFilter, setSpecificValueFilter] = React.useState<string>("all");
  const [selectedPreDispatchMessageIndex, setSelectedPreDispatchMessageIndex] = React.useState<
    number | null
  >(null);

  const messagesWithMetadata = React.useMemo(
    () =>
      preDispatchMessages.map((entry) => ({
        entry,
        metadata: getDecodedMessageMetadata(entry.schemaFrontEndMessageJson),
      })),
    [preDispatchMessages],
  );

  let activeMessageTypeFilter = "all";
  if (contentTypeFilter === "OneHotMessage") {
    activeMessageTypeFilter = oneHotMessageTypeFilter;
  } else if (contentTypeFilter === "BitMaskMessage") {
    activeMessageTypeFilter = bitMaskMessageTypeFilter;
  }

  // Normalized value of whichever filter (liftId or bankId) is currently active.
  // Everything downstream (capture gate, list filter, specific-value options)
  // keys off this instead of `preDispatchLiftFilter` directly, so switching modes
  // swaps the identity used to match messages without duplicating the filtering
  // logic per mode.
  const activePreDispatchFilterValue = (
    preDispatchFilterMode === "bankId" ? preDispatchBankFilter : preDispatchLiftFilter
  )
    .trim()
    .toLowerCase();

  const getEntryFilterValue = React.useCallback(
    (entry: PreDispatchMessage) =>
      (preDispatchFilterMode === "bankId" ? entry.bankId : entry.liftId).trim().toLowerCase(),
    [preDispatchFilterMode],
  );

  const filteredPreDispatchMessages = React.useMemo(() => {
    if (!activePreDispatchFilterValue) return [];

    return messagesWithMetadata
      .filter(({ entry, metadata }) => {
        if (getEntryFilterValue(entry) !== activePreDispatchFilterValue) return false;
        if (contentTypeFilter !== "all" && metadata.contentType !== contentTypeFilter) return false;
        if (
          activeMessageTypeFilter !== "all" &&
          metadata.messageTypeName !== activeMessageTypeFilter
        )
          return false;
        if (specificValueFilter !== "all") {
          if (!metadata.specificValueName) return false;
          return metadata.specificValueName === specificValueFilter;
        }
        return true;
      })
      .map(({ entry }) => entry);
  }, [
    messagesWithMetadata,
    activePreDispatchFilterValue,
    getEntryFilterValue,
    contentTypeFilter,
    activeMessageTypeFilter,
    specificValueFilter,
  ]);

  const specificValueOptions = React.useMemo(() => {
    if (!activePreDispatchFilterValue || contentTypeFilter === "all") return [];

    const values = new Set<string>();

    messagesWithMetadata.forEach(({ entry, metadata }) => {
      if (getEntryFilterValue(entry) !== activePreDispatchFilterValue) return;
      if (metadata.contentType !== contentTypeFilter) return;
      if (activeMessageTypeFilter !== "all" && metadata.messageTypeName !== activeMessageTypeFilter)
        return;
      if (metadata.specificValueName) values.add(metadata.specificValueName);
    });

    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [
    messagesWithMetadata,
    activePreDispatchFilterValue,
    getEntryFilterValue,
    contentTypeFilter,
    activeMessageTypeFilter,
  ]);

  React.useEffect(() => {
    setSpecificValueFilter("all");
    if (contentTypeFilter === "all") {
      setOneHotMessageTypeFilter("all");
      setBitMaskMessageTypeFilter("all");
    }
  }, [contentTypeFilter]);

  React.useEffect(() => {
    setSpecificValueFilter("all");
  }, [oneHotMessageTypeFilter, bitMaskMessageTypeFilter]);

  React.useEffect(() => {
    if (specificValueFilter === "all") return;
    const exists = specificValueOptions.some((option) => option.value === specificValueFilter);
    if (!exists) {
      setSpecificValueFilter("all");
    }
  }, [specificValueFilter, specificValueOptions]);

  React.useEffect(() => {
    if (filteredPreDispatchMessages.length === 0) {
      setSelectedPreDispatchMessageIndex(null);
      return;
    }
    if (
      selectedPreDispatchMessageIndex === null ||
      selectedPreDispatchMessageIndex >= filteredPreDispatchMessages.length
    ) {
      setSelectedPreDispatchMessageIndex(filteredPreDispatchMessages.length - 1);
    }
  }, [filteredPreDispatchMessages, selectedPreDispatchMessageIndex]);

  const selectedFilteredPreDispatchMessage = React.useMemo(() => {
    if (selectedPreDispatchMessageIndex === null) return null;
    return filteredPreDispatchMessages[selectedPreDispatchMessageIndex] ?? null;
  }, [filteredPreDispatchMessages, selectedPreDispatchMessageIndex]);

  const latestSchemaFrontEndPrettyJson = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return "";
    try {
      const schemaFrontEndMessage = JSON.parse(
        selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson,
      );
      return JSON.stringify(omitBankIdAndLiftId(schemaFrontEndMessage), null, 2);
    } catch {
      return selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson;
    }
  }, [selectedFilteredPreDispatchMessage]);

  const latestSchemaFrontEndTranslatedPrettyJson = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return "";
    try {
      const schemaFrontEndMessage = JSON.parse(
        selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson,
      );
      return JSON.stringify(
        omitBankIdAndLiftId(translateDecodedPayloadEnums(schemaFrontEndMessage)),
        null,
        2,
      );
    } catch {
      return selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson;
    }
  }, [selectedFilteredPreDispatchMessage]);

  const selectedServiceModeInfo = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return null;
    return getServiceModeTagInfo(selectedFilteredPreDispatchMessage.socketMessageLiftJson);
  }, [selectedFilteredPreDispatchMessage]);

  const selectedBitMaskTrafficPosition = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return null;

    try {
      const parsed = JSON.parse(
        selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson,
      ) as Record<string, unknown>;
      const msg = parsed.msg as Record<string, unknown> | undefined;
      const encoded = msg?.msg as Record<string, unknown> | undefined;
      const contentType = encoded?.contentType;

      if (
        contentType !== EncodedMessageContent.BitMaskMessage &&
        contentType !== "BitMaskMessage"
      ) {
        return null;
      }

      const content = encoded?.content as Record<string, unknown> | undefined;
      const messageType = content?.messageType;
      if (
        messageType !== MessageContent.Traffic_TrafficMessage &&
        messageType !== "Traffic_TrafficMessage"
      ) {
        return null;
      }

      // ElevatorTrafficMessage carries `position` as a sibling of the
      // `traffic` variant field (NormalTraffic/ValuedTraffic), not nested
      // inside it.
      const trafficDevice = content?.message as Record<string, unknown> | undefined;
      const position = trafficDevice?.position;
      if (typeof position === "number" || typeof position === "string") {
        return position;
      }
      return null;
    } catch {
      return null;
    }
  }, [selectedFilteredPreDispatchMessage]);

  const selectedMessageContentType = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return null;
    return getContentTypeFromSchemaJson(
      selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson,
    );
  }, [selectedFilteredPreDispatchMessage]);

  const jsonBoxHighlightStyle = React.useMemo(() => {
    if (selectedMessageContentType === "OneHotMessage") {
      return {
        border: "1px solid rgba(255, 77, 79, 0.55)",
        background: "rgba(255, 77, 79, 0.07)",
      };
    }
    if (selectedMessageContentType === "BitMaskMessage") {
      return {
        border: "1px solid rgba(82, 196, 26, 0.55)",
        background: "rgba(82, 196, 26, 0.08)",
      };
    }
    return {
      border: "1px solid #d9d9d9",
      background: "transparent",
    };
  }, [selectedMessageContentType]);

  const onDownloadPreDispatchMessages = React.useCallback(() => {
    const exportPayload = filteredPreDispatchMessages.map((entry) => {
      try {
        const schemaFrontEndMessage = JSON.parse(entry.schemaFrontEndMessageJson);
        return {
          schemaFrontEndMessage: omitBankIdAndLiftId(schemaFrontEndMessage),
          schemaFrontEndMessageTranslated: omitBankIdAndLiftId(
            translateDecodedPayloadEnums(schemaFrontEndMessage),
          ),
        };
      } catch {
        return {
          schemaFrontEndMessage: entry.schemaFrontEndMessageJson,
          schemaFrontEndMessageTranslated: entry.schemaFrontEndMessageJson,
        };
      }
    });

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `decoded_front_end_messages_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [filteredPreDispatchMessages]);

  React.useEffect(() => {
    const onPreDispatchMessage = (event: {
      timestamp?: number;
      bankId?: string;
      liftId?: string;
      schemaFrontEndMessageJson?: string;
      socketMessageLiftJson?: string;
    }) => {
      if (!activePreDispatchFilterValue) return;

      // In "bankId" mode we deliberately capture every lift under that bank
      // (instead of a single liftId) so we can see, side by side, whether a
      // message family (e.g. hall calls) ever reaches the front end for the
      // bank at all, versus being dropped/filtered for one particular lift.
      const incomingValue = (preDispatchFilterMode === "bankId" ? event?.bankId : event?.liftId)
        ?.trim()
        .toLowerCase();
      if (incomingValue !== activePreDispatchFilterValue) return;

      const schemaFrontEndMessageJson = event?.schemaFrontEndMessageJson ?? "";
      const socketMessageLiftJson = event?.socketMessageLiftJson ?? "";
      if (!schemaFrontEndMessageJson || !socketMessageLiftJson) return;

      setPreDispatchMessages((prev) => [
        ...prev,
        {
          timestamp: event.timestamp ?? Date.now(),
          bankId: event.bankId ?? "",
          liftId: event.liftId ?? "",
          schemaFrontEndMessageJson,
          socketMessageLiftJson,
        },
      ]);
    };

    socketEmitter.onPreDispatchMessage(onPreDispatchMessage);
    return () => {
      socketEmitter.offPreDispatchMessage(onPreDispatchMessage);
    };
  }, [preDispatchFilterMode, activePreDispatchFilterValue]);

  React.useEffect(() => {
    setPreDispatchMessages([]);
    setSelectedPreDispatchMessageIndex(null);
  }, [preDispatchFilterMode, activePreDispatchFilterValue]);

  const serviceModeInfoTooltip = (
    <div style={{ maxWidth: 420, fontSize: 12, lineHeight: 1.5 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Which messages can update this tag:</div>
      <div>
        - contentType = <code>OneHotMessage</code>
      </div>
      <div>
        - messageType = <code>Service_ServiceMessage</code>
      </div>
      <div>
        - inside message, it uses <code>message.message</code> (service code)
      </div>
      <div>
        Example: <code>EscalatorServiceMessage</code> + <code>message: 0</code> =&gt;{" "}
        <code>AUTO</code>
      </div>
      <div>
        Other <code>contentType</code> values, such as <code>BitMaskMessage</code>, do not update
        this service mode tag.
      </div>
    </div>
  );

  return (
    <Modal
      title="Decoded FrontEnd Messages"
      open={open}
      onCancel={onClose}
      width={1400}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: "80vh", overflow: "hidden" }}
      footer={[
        <Button
          key="download"
          onClick={onDownloadPreDispatchMessages}
          disabled={filteredPreDispatchMessages.length === 0}
        >
          Download JSON
        </Button>,
        <Button
          key="clear"
          onClick={() => {
            setPreDispatchMessages([]);
            setSelectedPreDispatchMessageIndex(null);
          }}
        >
          Clear
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 600 }}>Filter by</div>
          <Radio.Group
            value={preDispatchFilterMode}
            onChange={(e: RadioChangeEvent) =>
              setPreDispatchFilterMode(e.target.value as PreDispatchFilterMode)
            }
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="liftId">liftId</Radio.Button>
            <Radio.Button value="bankId">bankId (all lifts)</Radio.Button>
          </Radio.Group>
        </div>
        {preDispatchFilterMode === "bankId" ? (
          <Input
            placeholder="Filter by bankId"
            value={preDispatchBankFilter}
            onChange={(e) => setPreDispatchBankFilter(e.target.value)}
          />
        ) : (
          <Input
            placeholder="Filter by liftId"
            value={preDispatchLiftFilter}
            onChange={(e) => setPreDispatchLiftFilter(e.target.value)}
          />
        )}
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        <div>
          Messages received for this {preDispatchFilterMode === "bankId" ? "bank" : "lift"}:{" "}
          <strong>{filteredPreDispatchMessages.length}</strong>
        </div>
        <div style={{ opacity: 0.75 }}>
          This debug view stores messages only while a{" "}
          {preDispatchFilterMode === "bankId" ? "bankId" : "liftId"} filter is set.
          {preDispatchFilterMode === "bankId" &&
            " Messages from every lift in the bank are captured together, so you can confirm whether a message family (e.g. hall calls) is arriving on the wire at all."}
        </div>
      </div>

      <div
        className={`decodedFrontEndMessages__panel__${theme}`}
        style={{
          marginTop: 12,
          borderRadius: 8,
          padding: 10,
          display: "grid",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Service mode tag (debug)</div>
            {selectedServiceModeInfo ? (
              <Tooltip placement="top" title={selectedServiceModeInfo.displayName}>
                <Tag color={selectedServiceModeInfo.color || "default"}>
                  {selectedServiceModeInfo.abbreviation.substring(0, 4)}
                </Tag>
              </Tooltip>
            ) : (
              <Tag color="default">N/A</Tag>
            )}
            <Tooltip placement="topLeft" title={serviceModeInfoTooltip}>
              <InfoCircleOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} />
            </Tooltip>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Traffic position (debug)</div>
            {selectedBitMaskTrafficPosition !== null ? (
              <Tag color="processing">{selectedBitMaskTrafficPosition}</Tag>
            ) : (
              <Tag color="default">N/A</Tag>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 600 }}>contentType filter</div>
            <Radio.Group
              value={contentTypeFilter}
              onChange={(e: RadioChangeEvent) =>
                setContentTypeFilter(e.target.value as ContentTypeFilter)
              }
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="all">All messages</Radio.Button>
              <Radio.Button value="OneHotMessage">OneHotMessage</Radio.Button>
              <Radio.Button value="BitMaskMessage">BitMaskMessage</Radio.Button>
            </Radio.Group>
          </div>

          {contentTypeFilter === "OneHotMessage" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 600 }}>messageType filter</div>
              <Select
                value={oneHotMessageTypeFilter}
                onChange={setOneHotMessageTypeFilter}
                options={ONE_HOT_MESSAGE_TYPE_OPTIONS}
                style={{ minWidth: 280 }}
              />
            </div>
          )}

          {contentTypeFilter === "BitMaskMessage" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 600 }}>messageType filter</div>
              <Select
                value={bitMaskMessageTypeFilter}
                onChange={setBitMaskMessageTypeFilter}
                options={BIT_MASK_MESSAGE_TYPE_OPTIONS}
                style={{ minWidth: 360 }}
              />
            </div>
          )}

          {contentTypeFilter !== "all" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 600 }}>specific value filter</div>
              <Select
                value={specificValueFilter}
                onChange={setSpecificValueFilter}
                options={[{ value: "all", label: "All values" }, ...specificValueOptions]}
                style={{ minWidth: 320 }}
                showSearch
                optionFilterProp="label"
              />
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "260px minmax(0, 1fr) minmax(0, 1fr)",
          gap: 12,
          marginTop: 12,
          alignItems: "start",
          maxHeight: "62vh",
        }}
      >
        <div
          className={`decodedFrontEndMessages__panel__${theme}`}
          style={{
            borderRadius: 8,
            padding: 10,
            minHeight: 220,
            maxHeight: "56vh",
            overflowY: "auto",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Messages</div>
          <div style={{ display: "grid", gap: 6 }}>
            {filteredPreDispatchMessages.length === 0 ? (
              <div style={{ opacity: 0.75, fontSize: 12 }}>
                No messages for current {preDispatchFilterMode === "bankId" ? "bankId" : "liftId"}{" "}
                filter.
              </div>
            ) : (
              filteredPreDispatchMessages.map((entry, index) => {
                const isSelected = selectedPreDispatchMessageIndex === index;
                return (
                  <button
                    key={`${entry.timestamp}-${index}`}
                    type="button"
                    onClick={() => setSelectedPreDispatchMessageIndex(index)}
                    className={
                      isSelected
                        ? `decodedFrontEndMessages__item__selected__${theme}`
                        : `decodedFrontEndMessages__item__${theme}`
                    }
                    style={{
                      textAlign: "left",
                      borderRadius: 6,
                      padding: "8px 10px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>Message {index + 1}</div>
                    {/* In bankId mode, messages from several lifts interleave here — the
                        liftId tag is what lets you tell them apart at a glance. */}
                    {preDispatchFilterMode === "bankId" && (
                      <div style={{ fontSize: 11, opacity: 0.75 }}>liftId: {entry.liftId}</div>
                    )}
                    <div style={{ fontSize: 11, opacity: 0.75 }}>
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div
          style={{
            ...jsonBoxHighlightStyle,
            borderRadius: 8,
            padding: 10,
            minHeight: 220,
            maxHeight: "56vh",
            overflowY: "auto",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>schemaFrontEndMessage</div>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
            {latestSchemaFrontEndPrettyJson || "No messages yet for this liftId filter."}
          </pre>
        </div>

        <div
          style={{
            ...jsonBoxHighlightStyle,
            borderRadius: 8,
            padding: 10,
            minHeight: 220,
            maxHeight: "56vh",
            overflowY: "auto",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>schemaFrontEndMessageTranslated</div>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
            {latestSchemaFrontEndTranslatedPrettyJson || "No messages yet for this liftId filter."}
          </pre>
        </div>
      </div>
    </Modal>
  );
}

export default DecodedFrontEndMessagesModal;
